import { useState, useEffect, useCallback } from 'react';
import { Wallet, Search, Plus, Eye, Receipt, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { revenueAPI, manufacturersAPI, vendorsAPI } from '@/services/api';

const ReceivablePaymentsPage = () => {
  const { toast } = useToast();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Party selection
  const [partyType, setPartyType] = useState('manufacturer');
  const [partyId, setPartyId] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // Pending invoices for selected party
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState({});
  
  // Payment form
  const [paymentMode, setPaymentMode] = useState('cash');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await revenueAPI.listPayments({});
      setPayments(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch payments', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchMetadata = async () => {
    try {
      const [mfrsRes, vendorsRes] = await Promise.all([
        manufacturersAPI.dropdown(),
        vendorsAPI.list()
      ]);
      setManufacturers(mfrsRes.data || []);
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchMetadata();
  }, [fetchPayments]);

  const fetchPendingInvoices = async () => {
    if (!partyId) {
      setPendingInvoices([]);
      return;
    }
    
    try {
      const response = await revenueAPI.listInvoices({
        party_type: partyType,
        party_id: partyId,
        status: 'pending'
      });
      
      // Also fetch partial invoices
      const partialRes = await revenueAPI.listInvoices({
        party_type: partyType,
        party_id: partyId,
        status: 'partial'
      });
      
      const allPending = [...(response.data || []), ...(partialRes.data || [])];
      setPendingInvoices(allPending);
      setSelectedInvoices({});
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch pending invoices', variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (paymentDialogOpen && partyId) {
      fetchPendingInvoices();
    }
  }, [partyId, partyType, paymentDialogOpen]);

  const handleOpenPaymentDialog = () => {
    setPartyType('manufacturer');
    setPartyId('');
    setPendingInvoices([]);
    setSelectedInvoices({});
    setPaymentMode('cash');
    setReference('');
    setNotes('');
    setPaymentDialogOpen(true);
  };

  const handleInvoiceSelect = (invoice, amount) => {
    const newSelected = { ...selectedInvoices };
    if (amount && amount > 0) {
      newSelected[invoice.id] = {
        invoice_id: invoice.id,
        invoice_number: invoice.invoice_number,
        allocated_amount: Math.min(amount, invoice.balance_amount)
      };
    } else {
      delete newSelected[invoice.id];
    }
    setSelectedInvoices(newSelected);
  };

  const getTotalAllocated = () => {
    return Object.values(selectedInvoices).reduce((sum, inv) => sum + (inv.allocated_amount || 0), 0);
  };

  const handleSavePayment = async () => {
    const allocations = Object.values(selectedInvoices).filter(a => a.allocated_amount > 0);
    
    if (allocations.length === 0) {
      toast({ title: 'Error', description: 'Please select at least one invoice and enter amount', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      await revenueAPI.createPayment({
        party_type: partyType,
        party_id: partyId,
        invoice_allocations: allocations,
        payment_mode: paymentMode,
        reference: reference || null,
        notes: notes || null
      });
      
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      fetchPayments();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to record payment', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleView = (payment) => {
    setSelectedPayment(payment);
    setViewDialogOpen(true);
  };

  const getPartyName = () => {
    if (partyType === 'manufacturer') {
      const mfr = manufacturers.find(m => m.id === partyId);
      return mfr?.company_name || '';
    } else {
      const vendor = vendors.find(v => v.id === partyId);
      return vendor?.name || '';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="receivable-payments-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Wallet className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Receivable Payments</h1>
            <p className="text-gray-500 text-sm">Collect payments from vendors and manufacturers</p>
          </div>
        </div>
        <Button onClick={handleOpenPaymentDialog} className="bg-green-600 hover:bg-green-700" data-testid="collect-payment-btn">
          <Plus className="w-4 h-4 mr-2" /> Collect Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Total Collected (Today)</p>
            <p className="text-2xl font-bold text-green-700">
              ₹{payments.filter(p => p.payment_date === new Date().toISOString().split('T')[0]).reduce((sum, p) => sum + p.total_amount, 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Total Payments</p>
            <p className="text-2xl font-bold text-blue-700">{payments.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-purple-600">Total Collected (All Time)</p>
            <p className="text-2xl font-bold text-purple-700">
              ₹{payments.reduce((sum, p) => sum + p.total_amount, 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payments Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : payments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Wallet className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No Payments Found</p>
              <p className="text-sm">Collected payments will appear here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-gray-600">Receipt #</TableHead>
                  <TableHead className="text-gray-600">Date</TableHead>
                  <TableHead className="text-gray-600">Party</TableHead>
                  <TableHead className="text-gray-600">Mode</TableHead>
                  <TableHead className="text-gray-600">Invoices</TableHead>
                  <TableHead className="text-gray-600 text-right">Amount</TableHead>
                  <TableHead className="text-gray-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="border-gray-200 hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono font-medium text-gray-800">{payment.payment_number}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{new Date(payment.payment_date).toLocaleDateString()}</span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-800">{payment.party_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{payment.party_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-gray-50 capitalize">{payment.payment_mode}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{payment.invoice_allocations?.length || 0} invoice(s)</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-bold text-green-600">₹{payment.total_amount?.toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(payment)}>
                        <Eye className="w-4 h-4 text-gray-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Collect Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <CreditCard className="w-5 h-5 text-green-600" />
              Collect Payment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Party Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Party Type</Label>
                <Select value={partyType} onValueChange={(v) => { setPartyType(v); setPartyId(''); }}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="manufacturer">Manufacturer</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Select {partyType === 'manufacturer' ? 'Manufacturer' : 'Vendor'}</Label>
                <Select value={partyId} onValueChange={setPartyId}>
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder={`Select ${partyType}`} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {partyType === 'manufacturer' ? (
                      manufacturers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.company_name}</SelectItem>
                      ))
                    ) : (
                      vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pending Invoices */}
            {partyId && (
              <div className="space-y-2">
                <Label className="text-gray-700">Pending Invoices</Label>
                {pendingInvoices.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                    No pending invoices for this party
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-right">Pay Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingInvoices.map((inv) => (
                          <TableRow key={inv.id}>
                            <TableCell>
                              <Checkbox
                                checked={!!selectedInvoices[inv.id]}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    handleInvoiceSelect(inv, inv.balance_amount);
                                  } else {
                                    handleInvoiceSelect(inv, 0);
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                            <TableCell>
                              <span className="text-xs capitalize">{inv.type?.replace(/_/g, ' ')}</span>
                            </TableCell>
                            <TableCell className="text-right">₹{inv.grand_total?.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-red-600">₹{inv.balance_amount?.toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                min="0"
                                max={inv.balance_amount}
                                value={selectedInvoices[inv.id]?.allocated_amount || ''}
                                onChange={(e) => handleInvoiceSelect(inv, parseFloat(e.target.value) || 0)}
                                className="w-28 text-right bg-gray-50 border-gray-300"
                                placeholder="0"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Payment Details */}
            {Object.keys(selectedInvoices).length > 0 && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-green-800">Total Payment Amount</span>
                  <span className="text-2xl font-bold text-green-700">₹{getTotalAllocated().toLocaleString()}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Payment Mode</Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                      <SelectTrigger className="bg-white border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Reference (Cheque/UTR)</Label>
                    <Input
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="Enter reference number"
                      className="bg-white border-gray-300"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-700">Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes..."
                    className="bg-white border-gray-300"
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleSavePayment} 
              disabled={saving || getTotalAllocated() === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-payment-btn"
            >
              {saving ? 'Processing...' : `Collect ₹${getTotalAllocated().toLocaleString()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Payment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Receipt className="w-5 h-5 text-green-600" />
              Payment Receipt
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Receipt Number</p>
                <p className="text-xl font-mono font-bold text-green-700">{selectedPayment.payment_number}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Party</p>
                  <p className="font-medium">{selectedPayment.party_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{new Date(selectedPayment.payment_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment Mode</p>
                  <p className="font-medium capitalize">{selectedPayment.payment_mode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Reference</p>
                  <p className="font-medium">{selectedPayment.reference || '-'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-2">Invoice Allocations</p>
                <div className="space-y-2">
                  {selectedPayment.invoice_allocations?.map((alloc, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                      <span className="font-mono text-sm">{alloc.invoice_number}</span>
                      <span className="font-medium">₹{alloc.allocated_amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-green-100 rounded-lg">
                <span className="font-medium text-green-800">Total Amount</span>
                <span className="text-2xl font-bold text-green-700">₹{selectedPayment.total_amount?.toLocaleString()}</span>
              </div>
              
              {selectedPayment.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-gray-700">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceivablePaymentsPage;
